// import packages or dependencies
import { memo, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import {
  Container,
  Box,
  SvgIcon,
  useTheme,
  Popover,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ReactComponent as FabIcon } from "../../../../../assets/icons/fab-icon.svg";
import { ReactComponent as PlusIcon } from "../../../../../assets/icons/plus-icon.svg";
import { openCreateaUserFormAction } from "../../userManagementSlice";

// custom styles for mui components
const useStyles = makeStyles((theme) => ({
  popoverStyle: {
    "& .css-xq1sd0-MuiPaper-root-MuiPopover-paper": {
      backdropFilter: "blur(6px)",
      backgroundColor: "rgba(255, 255, 255, 0.5)",
      borderRadius: "8px",
      color: theme.palette.text.secondary,
    },
  },
  listItem: {
    "& span": {
      fontSize: "12px",
    },
  },
}));

// reponsive menu button items
const menuItem = [
  {
    name: "Create User",
    icon: PlusIcon,
  },
];

/**
 * @returns This component returns the mobile-sized CreateUserForm form container
 */
const ResponsiveCreateUser = () => {
  // states and handlers
  const classes = useStyles();

  const theme = useTheme();

  const dispatch = useAppDispatch();

  const { createFolderState, uploadFileState } = useAppSelector(
    (state) => state
  );

  const [popover, setPopover] = useState<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (
      uploadFileState.openModal === true ||
      createFolderState.openModal === true
    ) {
      handleClose();
    }
  }, [createFolderState.openModal, uploadFileState.openModal]);

  const handleClick = (event: any) => {
    setPopover(event.currentTarget);
  };

  const handleClose = () => {
    setPopover(null);
  };

  const openPopover = Boolean(popover);

  const id = openPopover ? "popover" : undefined;

  // render jsx
  return (
    <Container
      maxWidth={false}
      sx={{
        width: "fit-content",
        height: "fit-content",
        display: "flex",
        flexDirection: "column",
        p: { xs: 0 },
        position: "fixed",
        bottom: "16px",
        right: "30px",
        zIndex:
          createFolderState.openModal || uploadFileState.openModal ? 1 : 1050,
        [theme.breakpoints.up("sm")]: {
          display: "none",
        },
      }}
    >
      <SvgIcon
        component={FabIcon}
        inheritViewBox
        sx={{
          width: "40px",
          height: "40px",
          color: theme.palette.secondary.main,
          cursor: "pointer",
          borderRadius: "50%",
          boxShadow: 3,
          transform: popover === null ? "rotate(0deg)" : "rotate(45deg)",
          transition: "all 0.5s",
        }}
        onClick={(event: any) => handleClick(event)}
      />

      <Popover
        id={id}
        open={openPopover}
        anchorEl={popover}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transitionDuration={{
          enter: 500,
          exit:
            createFolderState.openModal || uploadFileState.openModal ? 0 : 500,
        }}
        sx={{
          m: "-5px 0px 0px 0px",
          display:
            uploadFileState.openModal === true ||
            createFolderState.openModal === true
              ? "none"
              : "block",
          [theme.breakpoints.up("sm")]: {
            display: "none",
          },
        }}
        className={classes.popoverStyle}
      >
        <Box
          sx={{
            width: "150px",
            height: "65px",
            p: "16px",
          }}
        >
          {menuItem.map((item, index) => (
            <ListItem
              key={index}
              disablePadding
              sx={{
                display: "block",
              }}
              className={classes.listItem}
            >
              <ListItemButton
                sx={{
                  borderRadius: "5px",
                  justifyContent: "initial",
                  p: "4px",
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  switch (index) {
                    case 0:
                      dispatch(
                        openCreateaUserFormAction({
                          type: "create",
                          formIsOpen: true,
                        })
                      );
                      break;
                    default:
                      break;
                  }
                  handleClose();
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: "8px",
                    justifyContent: "center",
                  }}
                >
                  <SvgIcon
                    component={item.icon}
                    inheritViewBox
                    sx={{
                      width: "18px",
                      color: theme.palette.text.secondary,
                      stroke: (theme) => theme.palette.tertiary?.contrastText,
                    }}
                  />
                </ListItemIcon>
                <ListItemText primary={item.name} sx={{ display: "block" }} />
              </ListItemButton>
            </ListItem>
          ))}
        </Box>
      </Popover>
    </Container>
  );
};

export default memo(ResponsiveCreateUser);
